import { jsx as _jsx } from "react/jsx-runtime";
import { getMember, isMemberSignedIn, loadMember } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { forwardRef, useEffect, useState } from 'react';
import { useFormValidator } from '../../hooks/use-form-validator';
import { MemberForm } from '../../subcomponents/member-form/member-form';
import { MemberFormContextInstance } from '../../subcomponents/member-form/types/context';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { defaultUpdateMemberDetailsFormData } from './types/data';
import { defaultUpdateMemberDetailsFormProps, updateMemberDetailsFormPropsValidator } from './types/props';
import { getInitialMemberFormData } from './utils/get-initial-member-form-data';
import { getNotificationDetailsForError } from './utils/get-notification-details-for-error';
import { updatePersonalDetailsFromFormData } from './utils/update-personal-details-from-form-data';
const b = bemClassBuilder('v-update-member-details-form');
const UpdateMemberDetailsFormBase = (props, ref) => {
    const { assets, displayText, config, events, children, formId } = props;
    const [formData, setFormData] = useState(defaultUpdateMemberDetailsFormData);
    const { validationResult, validator, setValidationResult } = useFormValidator(config.validation);
    const isSignedIn = isMemberSignedIn();
    useEffect(() => {
        (async () => {
            await loadMember();
            const initialData = getInitialMemberFormData();
            if (initialData)
                setFormData(initialData);
        })();
    }, [isSignedIn]);
    const handleSubmit = async () => {
        await updatePersonalDetailsFromFormData(formData);
    };
    const handleAfterSuccessfulSubmit = async () => {
        var _a;
        const member = getMember();
        if (member)
            await ((_a = events.onAfterSuccessfulSubmit) === null || _a === void 0 ? void 0 : _a.call(events, member));
    };
    const clearValidationErrors = () => setValidationResult({
        isValid: true,
        validationErrors: {}
    });
    const setFieldValue = (fieldNameInCamelCase, newValue) => {
        setFormData(data => (Object.assign(Object.assign({}, data), { [fieldNameInCamelCase]: newValue })));
    };
    const context = {
        formData,
        setFormData,
        setFieldValue,
        validationResult,
        assets,
        displayText,
        config,
        events: Object.assign(Object.assign({}, events), { onSubmit: handleSubmit, onAfterSuccessfulSubmit: handleAfterSuccessfulSubmit }),
        validator
    };
    return (_jsx("div", Object.assign({ className: b.block() }, { children: _jsx(MemberFormContextInstance.Provider, Object.assign({ value: context }, { children: _jsx(MemberForm, Object.assign({ ref: ref, clearValidationErrors: clearValidationErrors, formId: formId, getNotificationDetailsForError: () => getNotificationDetailsForError(displayText) }, { children: children })) })) })));
};
const UpdateMemberDetailsForm = observer(forwardRef(UpdateMemberDetailsFormBase));
UpdateMemberDetailsForm.displayName = 'UpdateMemberDetailsForm';
/**
 * The **Update Member Details Form** component allows a user to update their personal details. It is a wrapper for
 * the **Member Form** component.
 */
export const UpdateMemberDetailsFormComponent = withComponentWrapper(UpdateMemberDetailsForm, defaultUpdateMemberDetailsFormProps, updateMemberDetailsFormPropsValidator);
